import { compareDesc } from "date-fns";

import { createAppSelector, sectionUserSelectors } from "./base";
import {
  selectCurrentBreakoutGroupId,
  selectCurrentListenedBreakoutGroupId,
  selectIsInBreakout,
} from "./breakouts";
import { selectAvActiveClassUsersIds } from "./class-session";
import { selectCurrentClassUserId } from "./class-user";
import { selectMaxAvActiveParticipants } from "./org";
import { selectIsRecordingAgent } from "./permissions";
import {
  selectClassUserIdsPresentInClass,
  selectMaxParticipantBarSlots,
  selectRecentSpeakerClassUserIds,
} from "./pubsub-virtual-class-state";
import {
  selectClassUsersWithIsVisibleInClassCapability,
  selectScreensharingClassUserId,
} from "./virtual-classes";

import { ClassUserType, SectionRoleType } from "components/server-types";
import { isAvLimitActiveParticipantsEnabled } from "utils/waffle-flags";

const SECTION_ROLE_ORDERING: SectionRoleType[] = ["instructor", "section_admin", "student"];

export type SortVirtualClassUsersContext = {
  sectionUserMap: Record<string, { role?: SectionRoleType }>;
  screenshareClassUserId?: string | null;
  recentSpeakerIds?: string[];
  currentClassUserId?: string | null;
  roleOnly?: boolean;
};

export function sortVirtualClassUsers(
  users: ClassUserType[],
  ctx: SortVirtualClassUsersContext,
): ClassUserType[] {
  return users.toSorted((a, b) => {
    if (ctx.currentClassUserId) {
      if (a.id === ctx.currentClassUserId) return 1;
      if (b.id === ctx.currentClassUserId) return -1;
    }
    const roleA = ctx.sectionUserMap[a.section_user_id]?.role || "student";
    const roleB = ctx.sectionUserMap[b.section_user_id]?.role || "student";
    if (roleA !== roleB) {
      const aIdx = SECTION_ROLE_ORDERING.indexOf(roleA);
      const bIdx = SECTION_ROLE_ORDERING.indexOf(roleB);
      if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
      return aIdx >= 0 ? -1 : 1;
    }
    if (ctx.roleOnly) return a.section_user_id.localeCompare(b.section_user_id);
    if (a.hand_raised_at && b.hand_raised_at) {
      return compareDesc(b.hand_raised_at, a.hand_raised_at);
    }
    if (a.hand_raised_at) return -1;
    if (b.hand_raised_at) return 1;
    if (ctx.screenshareClassUserId) {
      if (a.id === ctx.screenshareClassUserId) return -1;
      if (b.id === ctx.screenshareClassUserId) return 1;
    }
    if (ctx.recentSpeakerIds?.length) {
      const aOrder = ctx.recentSpeakerIds.indexOf(a.id);
      const bOrder = ctx.recentSpeakerIds.indexOf(b.id);
      const aVal = aOrder === -1 ? Number.MAX_SAFE_INTEGER : aOrder;
      const bVal = bOrder === -1 ? Number.MAX_SAFE_INTEGER : bOrder;
      if (aVal !== bVal) return aVal - bVal;
    }
    const aSort = a.av_sort_order;
    const bSort = b.av_sort_order;
    if (aSort !== null && bSort !== null) return aSort - bSort;
    if (aSort !== null) return -1;
    if (bSort !== null) return 1;
    return a.section_user_id.localeCompare(b.section_user_id);
  });
}

// Source: visible by role + present. Main-call and filtered (breakout) selectors derive from this.
const selectPresentClassUsersVisibleByRole = createAppSelector(
  [selectClassUsersWithIsVisibleInClassCapability, selectClassUserIdsPresentInClass],
  (classUsersVisibleByRole, presentClassUserIds) =>
    classUsersVisibleByRole.filter((cu) => presentClassUserIds.includes(cu.id)),
);

// Only gets users that are in the main call
export const selectPresentClassUsersInMainCall = createAppSelector(
  [selectPresentClassUsersVisibleByRole],
  (visibleAndPresentClassUsers) =>
    visibleAndPresentClassUsers.filter((cu) => !cu.breakout_group_id),
);

// Gets users that are in the same room as the current user (breakout or main call)
export const selectPresentClassUsersFilteredByRoom = createAppSelector(
  [
    selectPresentClassUsersVisibleByRole,
    selectIsInBreakout,
    selectIsRecordingAgent,
    selectCurrentBreakoutGroupId,
  ],
  (visibleAndPresentClassUsers, isInBreakout, isRecordingAgent, currentBreakoutId) =>
    visibleAndPresentClassUsers.filter((classUser) => {
      if (!isInBreakout) return true;
      // Recording agents should not see anyone in a breakout
      if (isRecordingAgent) return false;
      if (currentBreakoutId) {
        return classUser.breakout_group_id === currentBreakoutId;
      }
      return classUser.breakout_group_id === null;
    }),
);

// Returns true if the current user is the leader of the participant bar - It will be the first user in the sorted list, usually an instructor.
export const selectIsParticipantBarLeader = createAppSelector(
  [selectPresentClassUsersVisibleByRole, selectCurrentClassUserId, (state) => state.section_users],
  (classUsersInVirtualClassPresent, currentClassUserId, sectionUserMap) => {
    if (classUsersInVirtualClassPresent.length === 0 || !currentClassUserId) return false;
    const orderedClassUsers = sortVirtualClassUsers(classUsersInVirtualClassPresent, {
      sectionUserMap,
      roleOnly: true,
    });
    return orderedClassUsers[0]?.id === currentClassUserId;
  },
);

export const selectClassUsersInListenedBreakoutGroup = createAppSelector(
  [selectPresentClassUsersVisibleByRole, selectCurrentListenedBreakoutGroupId],
  (classUsersVisibleByRoleAndPresent, listenedBreakoutGroupId) => {
    if (!listenedBreakoutGroupId) {
      return [];
    }
    return classUsersVisibleByRoleAndPresent.filter(
      (classUser) => classUser.breakout_group_id === listenedBreakoutGroupId,
    );
  },
);

export const selectAvActiveClassUsers = createAppSelector(
  [selectAvActiveClassUsersIds, selectPresentClassUsersVisibleByRole],
  (avActiveClassUsersIds, classUsersVisibleByRoleAndPresent) => {
    // returns the class users that are in the av active class users ids list
    return classUsersVisibleByRoleAndPresent.filter((classUser) =>
      avActiveClassUsersIds.includes(classUser.id),
    );
  },
);

// Calculated list for the active participants (main call only). Leader always computes this for main call and distributes it.
// Order: role, hand raised, screenshare first; then recent speakers (by recency); then av_sort_order, section_user_id.
export const selectCalculatedListForActiveParticipants = createAppSelector(
  [
    selectPresentClassUsersInMainCall,
    sectionUserSelectors.selectEntities,
    selectMaxAvActiveParticipants,
    selectScreensharingClassUserId,
    selectRecentSpeakerClassUserIds,
  ],
  (
    classUsersInMainCallPresent,
    sectionUserMap,
    maxAvActiveParticipants,
    screenshareClassUserId,
    recentSpeakerIds,
  ) => {
    const ordered = sortVirtualClassUsers(classUsersInMainCallPresent, {
      sectionUserMap,
      screenshareClassUserId: screenshareClassUserId ?? null,
      recentSpeakerIds,
    });
    if (isAvLimitActiveParticipantsEnabled()) {
      return ordered.slice(0, maxAvActiveParticipants).map((u) => u.id);
    }
    return ordered.map((u) => u.id);
  },
);

// In main call: use distributed AvActive list. In breakout: use local list (current breakout group).
const selectClassUsersForParticipantBarInput = createAppSelector(
  [selectIsInBreakout, selectAvActiveClassUsers, selectPresentClassUsersFilteredByRoom],
  (isInBreakout, avActiveClassUsers, filteredClassUsersInVirtualClass) =>
    isInBreakout ? filteredClassUsersInVirtualClass : avActiveClassUsers,
);

// This selector takes the active class users as input and applies the sorting logic that
// ensures that the most relevant participants occupy the available slots in the participant bar, the rest is returned but will be hidden in the bar.
// We sort by the original order but if the number of participants is greater than the available slots, we always prioritize the recent speakers, while keeping the order.
export const selectClassUsersForParticipantBar = createAppSelector(
  [
    selectClassUsersForParticipantBarInput,
    (state) => state.section_users,
    selectScreensharingClassUserId,
    selectRecentSpeakerClassUserIds,
    selectMaxParticipantBarSlots,
    selectCurrentClassUserId,
  ],
  (
    classUsersForParticipantBarInput,
    sectionUserMap,
    screenshareClassUserId,
    recentSpeakers,
    maxSlots,
    currentClassUserId,
  ) => {
    const ordered = sortVirtualClassUsers(classUsersForParticipantBarInput, {
      sectionUserMap,
      screenshareClassUserId: screenshareClassUserId ?? null,
      currentClassUserId: currentClassUserId ?? null,
    });

    // We can show all participants - keep the calculated order
    if (ordered.length <= maxSlots) {
      return ordered;
    }

    let currentUser: ClassUserType | undefined;
    if (currentClassUserId) {
      const idx = ordered.findIndex((u) => u.id === currentClassUserId);
      if (idx >= 0) {
        // Remove current user from ordered to handle them separately later
        [currentUser] = ordered.splice(idx, 1);
      }
    }

    const maxSlotsAdjustedWithLocal = maxSlots - (currentUser ? 1 : 0);

    // Get the visible participants prioritizing recent speakers. We keep the recentSpeakers order as is
    // since it is already by recency.
    let visible = recentSpeakers
      .map((id) => ordered.find((u) => u.id === id))
      // recent speakers may include users not in ordered
      .filter(Boolean)
      .slice(0, maxSlotsAdjustedWithLocal);

    // When possible, fill up the list with users from the original order, skipping those already included
    for (const user of ordered) {
      if (visible.length >= maxSlotsAdjustedWithLocal) break;
      if (!visible.some((u) => u.id === user.id)) {
        visible.push(user);
      }
    }
    // Now we have a combination of recent speakers and original order filling up the visible slots.
    // Re-sort the visible set by the original order to ensure consistency and avoid shuffling.
    visible = ordered
      .filter((u) => visible.some((v) => v.id === u.id))
      .slice(0, maxSlotsAdjustedWithLocal);

    if (currentUser) {
      // The currentUser will always be last
      visible.push(currentUser);
    }
    // We build the final list including the hidden but present participants.
    // These are all needed by the ParticipantBar component to render all participants.
    const visibleIds = new Set(visible.map((u) => u.id));
    const hidden = ordered.filter((u) => !visibleIds.has(u.id));

    return [...visible, ...hidden];
  },
);

// Returns class user ids who are hidden due to the size of the participant bar,
// not because they are in a different room
export const selectClassUserIdsHiddenInParticipantBar = createAppSelector(
  [selectClassUsersForParticipantBar, selectMaxParticipantBarSlots],
  (sortedClassUsers, visibleCount) =>
    sortedClassUsers.slice(visibleCount).map((classUser) => classUser.id),
);
